import { Button, Stack, VStack } from '@genial/components';
import { ErrorListResponse, instanceOfErrorListResponse, Service } from '@genial/services';
import { Utils } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import React, { useCallback, useState } from 'react';

import { useRecoilQuery } from '../../../../../../../../hooks/useRecoilQuery';
import { useToastCreator } from '../../../../../../../../hooks/useToastCreator';
import { SignedInNavigationScreenNavigationProp } from '../../../../../../../../routes/types';
import {
  ReturnTypeOf_InserirMovimentacaoClubes,
  ReturnTypeOf_ObterDatasClubes,
} from '../../../../../../../../store/product/external/types';
import { clubsDetailsQuery } from '../../../../../../../../store/product/selectors';
import {
  useClubsAssetController,
  useClubsDetailsController,
} from '../../../../../../../BatchTicket/components/DynamicAsset/hooks/products/Clubs';
import { CustomerDetailTabsKeys } from '../../../../../../constants';
import { CardHeader } from '../../components/CardHeader';
import { ConfirmationButton } from '../../components/ConfirmationButton';
import { TicketApplicationInProgress } from '../../components/TicketApplicationInProgress';
import { TicketApplicationSuccessDetails } from '../../components/TicketApplicationSuccessDetails';
import { blankTicketApplicationBaseInfo } from '../../components/TicketApplicationSuccessDetails/constants';
import { defaultErrorMessageApplication } from '../../constants';
import { CustomerApplicationModalProductProps } from '../../types';

import { ClubsDetailsCard } from './components/ClubsDetailsCard';
import { ClubsInvestCard } from './components/ClubsInvestCard';
import { getClubsSuccessInfos } from './constants';

export interface ClubsApplicationProps extends CustomerApplicationModalProductProps<'clubs'> {
  onClose: () => void;
}

export const ClubsApplication: React.FC<ClubsApplicationProps> = ({
  onClose,
  offer,
  accountId,
  accountNumber,
  customerId,
  customer,
  cpfCnpj,
}) => {
  const navigation = useNavigation<SignedInNavigationScreenNavigationProp>();

  const {
    data: details,
    error: detailsError,
    loading,
  } = useRecoilQuery(clubsDetailsQuery({ clubId: offer.clubId, accountId }));
  const [currentStep, setCurrentStep] = useState<'value' | 'details'>('value');
  const { showToastFromError, showToast } = useToastCreator('clubsApplicationInCustomer');
  const [sendingOrder, setSendingOrder] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successInfos, setSuccessInfos] = useState(blankTicketApplicationBaseInfo);
  const offerController = useClubsAssetController(offer);
  const detailsController = useClubsDetailsController(offer);
  const nextStepButtonDisabled = !!offerController.hasError && !loading && !detailsError;
  const confirmationOrderButtonDisabled =
    detailsController.hasError &&
    !loading &&
    !detailsError &&
    !detailsController.isAgreementChecked;

  const handleConfirmOrder = async () => {
    if (currentStep === 'value') {
      setCurrentStep('details');
      return;
    }

    const today = new Date();

    let currentSelectedDate = detailsController.selectedDate;
    let currentQuotationDate = details?.quotationDate;
    let currentSettlementDate = details?.settlementDate;

    if (currentSelectedDate && today.toDateString() !== currentSelectedDate.toDateString()) {
      const datasClubeData = (await Service.External.Extranet.Fundos.V1.fundosObterDatasClubes({
        idClube: offer.clubId,
        dataOperacao: Utils.Mask.DateFormat(currentSelectedDate, 'dd/MM/yyyy HH:mm'),
        tipoOperacao: 0,
        screenName: 'clubsApplicationModal',
      })) as Awaited<Promise<ReturnTypeOf_ObterDatasClubes>>;

      currentQuotationDate = new Date(datasClubeData.dataCotizacao);
      currentSettlementDate = new Date(datasClubeData.dataLiquidacao);
      currentSelectedDate = new Date(datasClubeData.dataOperacao);
    }

    setSendingOrder(true);
    Service.External.Extranet.Fundos.V1.fundosInserirMovimentacaoClubes({
      screenName: 'clubsApplicationModal',
      dto: {
        cpfCotista: cpfCnpj,
        idConta: accountId,
        valor: offerController.currentValue,
        idCarteira: offer.fundId,
        dataOperacao: currentSelectedDate,
        dataCotizacao: currentQuotationDate,
        dataLiquidacao: currentSettlementDate,
        tipoOperacao: 1, // Aplicação
        tipoResgate: 0, // Especifico
        idCanal: 3, // Extranet
      },
    })
      .then((data) => {
        setSuccess(true);
        setSuccessInfos(
          getClubsSuccessInfos(
            offer,
            offerController.currentValue,
            data as ReturnTypeOf_InserirMovimentacaoClubes
          )
        );
      })
      .catch(async (error) => {
        if (instanceOfErrorListResponse(error as object)) {
          (error as ErrorListResponse).errors?.forEach((errorItem) =>
            showToastFromError(errorItem)
          );
          return;
        }

        const jsonResponse = await error.response.json();

        if (jsonResponse?.message) {
          showToast('error', defaultErrorMessageApplication[1], jsonResponse.message);
          return;
        }

        showToast(...defaultErrorMessageApplication);
      })
      .finally(() => setSendingOrder(false));
  };

  const handleFollow = useCallback(() => {
    navigation.replace('AppDrawer', {
      screen: 'CustomerDetailGroup',
      params: {
        screen: 'CustomerDetail',
        params: {
          customer,
          selectedTab: CustomerDetailTabsKeys.Transactions,
          movementInitialTab: 'Clubs',
          customerId,
          accountId,
          accountNumber,
          cpfCnpj,
        },
      },
    });
  }, [accountId, accountNumber, cpfCnpj, customer, customerId, navigation]);

  if (success) {
    return (
      <TicketApplicationSuccessDetails
        {...successInfos}
        onClose={onClose}
        onFollow={handleFollow}
      />
    );
  }

  if (sendingOrder) {
    return <TicketApplicationInProgress />;
  }

  return (
    <VStack space="stack.xxxs">
      <CardHeader
        onClose={onClose}
        currentStep={currentStep === 'value' ? 1 : 2}
        totalSteps={2}
        hasSteps
      />
      {currentStep === 'value' && (
        <ClubsInvestCard
          offer={offer}
          offerController={offerController}
          accountId={accountId}
          details={details}
        />
      )}
      {currentStep === 'details' && (
        <ClubsDetailsCard
          offer={offer}
          details={details}
          detailsController={detailsController}
          offerController={offerController}
        />
      )}
      <Stack direction={{ base: 'column', md: 'row' }} space="stack.nano" width="100%">
        {currentStep === 'details' && (
          <Button variant="secondary" onPress={onClose} flex={1}>
            CANCELAR
          </Button>
        )}
        <ConfirmationButton
          onConfirm={handleConfirmOrder}
          flex={1}
          disabled={
            currentStep === 'value' ? nextStepButtonDisabled : confirmationOrderButtonDisabled
          }
        />
      </Stack>
    </VStack>
  );
};
